import React from 'react';

const th = 'px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider';
const td = 'px-3 py-2 text-sm text-gray-800 border-t border-gray-100';

function statusBadge(status) {
  if (status === 'valid') {
    return <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Se importará</span>;
  }
  if (status === 'empty') {
    return <span className="text-xs text-gray-500">Vacía</span>;
  }
  return <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">No válida</span>;
}

const ClassificationImportPreviewModal = ({
  isOpen,
  onClose,
  variant,
  preview,
  onConfirm,
  confirming,
  titleSuffix = '',
}) => {
  if (!isOpen || !preview?.success) return null;

  const { previewRows, validCount, emptyCount, invalidCount } = preview;
  const title =
    variant === 'categories'
      ? `Vista previa — importar categorías${titleSuffix}`
      : variant === 'subcategories'
        ? `Vista previa — importar subcategorías${titleSuffix}`
        : `Vista previa — importar familias${titleSuffix}`;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !confirming) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="classification-import-preview-title"
      >
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h3 id="classification-import-preview-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Revise los datos antes de confirmar. Solo las filas marcadas como «Se importará» se enviarán al servidor
            (el código lo genera el sistema).
          </p>
          <p className="text-sm mt-2">
            <span className="text-green-700 font-medium">{validCount}</span> a importar
            {emptyCount > 0 ? (
              <>
                {' · '}
                <span className="text-gray-500">{emptyCount} vacías (omitidas)</span>
              </>
            ) : null}
            {invalidCount > 0 ? (
              <>
                {' · '}
                <span className="text-red-700 font-medium">{invalidCount} con error</span>
              </>
            ) : null}
          </p>
        </div>

        <div className="overflow-auto flex-1 p-4">
          <table className="min-w-full border border-gray-200 rounded-md overflow-hidden">
            <thead className="bg-gray-50 sticky top-0">
              {variant === 'categories' ? (
                <tr>
                  <th className={th}>Fila Excel</th>
                  <th className={th}>Nombre</th>
                  <th className={th}>Descripción</th>
                  <th className={th}>Activo</th>
                  <th className={th}>Estado</th>
                  <th className={th}>Detalle</th>
                </tr>
              ) : variant === 'subcategories' ? (
                <tr>
                  <th className={th}>Fila Excel</th>
                  <th className={th}>Categoría</th>
                  <th className={th}>Nombre</th>
                  <th className={th}>Descripción</th>
                  <th className={th}>Activo</th>
                  <th className={th}>Estado</th>
                  <th className={th}>Detalle</th>
                </tr>
              ) : (
                <tr>
                  <th className={th}>Fila Excel</th>
                  <th className={th}>Categoría</th>
                  <th className={th}>Subcategoría</th>
                  <th className={th}>Nombre</th>
                  <th className={th}>Descripción</th>
                  <th className={th}>Activo</th>
                  <th className={th}>Estado</th>
                  <th className={th}>Detalle</th>
                </tr>
              )}
            </thead>
            <tbody className="bg-white">
              {previewRows.map((row) => (
                <tr key={row.excelRow} className={row.status === 'invalid' ? 'bg-red-50/50' : ''}>
                  <td className={td}>{row.excelRow}</td>
                  {variant === 'categories' ? (
                    <>
                      <td className={td}>{row.cols.nombre || '—'}</td>
                      <td className={td}>{row.cols.descripcion || '—'}</td>
                      <td className={td}>{row.cols.activo || '—'}</td>
                    </>
                  ) : variant === 'subcategories' ? (
                    <>
                      <td className={td}>{row.cols.categoria || '—'}</td>
                      <td className={td}>{row.cols.nombre || '—'}</td>
                      <td className={td}>{row.cols.descripcion || '—'}</td>
                      <td className={td}>{row.cols.activo || '—'}</td>
                    </>
                  ) : (
                    <>
                      <td className={td}>{row.cols.categoria || '—'}</td>
                      <td className={td}>{row.cols.subcategoria || '—'}</td>
                      <td className={td}>{row.cols.nombre || '—'}</td>
                      <td className={td}>{row.cols.descripcion || '—'}</td>
                      <td className={td}>{row.cols.activo || '—'}</td>
                    </>
                  )}
                  <td className={td}>{statusBadge(row.status)}</td>
                  <td className={`${td} text-gray-600 max-w-xs`}>{row.issue || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {previewRows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay filas de datos bajo los encabezados.</p>
          ) : null}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming || validCount === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirming ? 'Importando…' : `Confirmar importación (${validCount})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassificationImportPreviewModal;
